import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, AlertTriangle, ArrowLeft, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchForm } from "@/components/search/SearchForm";
import { ListingCard } from "@/components/search/ListingCard";
import { CompareBar, CompareDialog } from "@/components/search/Compare";
import { api, streamSearch, errMsg } from "@/lib/api";

const SOURCES = ["RockAuto", "AutoZone", "NAPA", "O'Reilly", "eBay Motors", "Amazon", "Dealer OEM"];

const Scanning = () => (
  <div className="flex flex-wrap gap-2 mt-4" data-testid="search-scanning">
    {SOURCES.map((s, i) => (
      <span key={s} className="font-mono text-[11px] uppercase tracking-wider px-2 py-1 border border-border rounded-sm text-muted-foreground animate-pulse" style={{ animationDelay: `${i * 0.15}s` }}>
        {s}
      </span>
    ))}
  </div>
);

const toForm = (q) => ({ year: q.year || "", make: q.make || "", model: q.model || "", part: q.part || "", part_number: q.part_number || "" });

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const woId = params.get("wo");
  const [wo, setWo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [initial, setInitial] = useState(null);

  const runSearch = async (q) => {
    setBusy(true); setSummary(""); setResult(null); setError(""); setSelected([]);
    try {
      await streamSearch(q, (ev) => {
        if (ev.type === "text") setSummary((s) => s + ev.content);
        if (ev.type === "results") {
          setResult(ev.search);
          setParams({ id: ev.search.id, ...(woId ? { wo: woId } : {}) }, { replace: true });
          toast.success(`${ev.search.listings.length} listings found`);
        }
        if (ev.type === "error") setError(ev.message);
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setSummary(""); setResult(null); setError(""); setSelected([]);
    const v = wo?.vehicle;
    setInitial(v ? toForm({ year: v.year, make: v.make, model: v.model }) : null); setFormKey((k) => k + 1);
    setParams(woId ? { wo: woId } : {});
  };

  useEffect(() => {
    if (woId) api.get(`/work-orders/${woId}`).then((r) => setWo(r.data)).catch(() => setWo(null));
    else setWo(null);
  }, [woId]);

  const addToWo = async (l) => {
    try {
      const { data } = await api.post(`/work-orders/${woId}/parts`, {
        description: l.title, part_number: l.part_number, qty: 1, unit_cost: l.price || 0, source_url: l.url, vendor: l.seller,
      });
      setWo(data);
      toast.success(`Added to WO #${data.number}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    const id = params.get("id");
    const rerun = params.get("rerun");
    if (!id) {
      if (params.get("make") || params.get("part") || params.get("part_number")) { setInitial(toForm(Object.fromEntries(params))); setFormKey((k) => k + 1); }
      return;
    }
    if (result?.id === id) return;
    api.get(`/searches/${id}`).then(({ data }) => {
      setInitial(toForm(data.query)); setFormKey((k) => k + 1);
      if (rerun) runSearch(data.query);
      else { setResult(data); setSummary(data.summary); }
    }).catch((e) => toast.error(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const listings = result?.listings || [];
  const priced = listings.filter((l) => l.price != null);
  const low = priced.length ? Math.min(...priced.map((l) => l.price)) : null;
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <>
      <div>
        {wo && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border border-primary/50 bg-primary/10 rounded-sm px-4 py-3 text-sm" data-testid="wo-parts-banner">
            <span>Adding parts to <b>WO #{wo.number}</b> — {wo.vehicle_label} · {wo.part_lines.length} part line(s)</span>
            <Link to={`/app/work-orders/${wo.id}`} className="flex items-center gap-1 text-primary font-semibold hover:text-orange-400" data-testid="back-to-wo-link"><ArrowLeft className="h-4 w-4" /> Back to work order</Link>
          </div>
        )}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-3">// AI parts search</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase">What are we hunting today?</h1>
          </div>
          {(result || summary) && !busy && (
            <Button variant="outline" onClick={reset} className="rounded-sm hover:text-white" data-testid="new-search-btn"><RotateCcw className="h-4 w-4 mr-1" /> New search</Button>
          )}
        </div>
        <SearchForm key={formKey} initial={initial} onSearch={runSearch} busy={busy} />

        {(busy || summary || error) && (
          <section className="mt-10 bg-card/80 border border-border rounded-sm p-6 relative overflow-hidden" data-testid="ai-summary-panel">
            {busy && <div className="absolute top-0 left-0 h-0.5 w-1/4 bg-primary scanbar" />}
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
              <Sparkles className="h-4 w-4 text-primary" /> {busy ? "AI is searching the web..." : "AI summary"}
            </div>
            {summary && <p className="whitespace-pre-wrap leading-relaxed text-[15px]" data-testid="ai-summary-text">{summary}</p>}
            {busy && !summary && <Scanning />}
            {busy && summary && <p className="mt-3 text-xs text-muted-foreground font-mono">Compiling listings…</p>}
            {error && <p className="flex items-center gap-2 text-red-400 text-sm mt-2" data-testid="search-error"><AlertTriangle className="h-4 w-4" />{error}</p>}
          </section>
        )}

        {result && (
          <section className="mt-10" data-testid="results-section">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <h2 className="text-3xl font-bold uppercase" data-testid="results-part-name">{result.part_name || "Listings"}</h2>
                {result.oem_numbers?.length > 0 && (
                  <p className="font-mono text-sm text-amber-400 uppercase tracking-wider mt-1" data-testid="results-oem-numbers">OEM: {result.oem_numbers.join(" · ")}</p>
                )}
              </div>
              <p className="font-mono text-xs text-muted-foreground" data-testid="results-count">{listings.length} LISTINGS FOUND</p>
            </div>
            {listings.length === 0 ? (
              <p className="text-muted-foreground" data-testid="no-results">No priced listings came back. Try adding the year/make/model or a part number.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {listings.map((l, i) => (
                  <ListingCard key={l.id} l={l} index={i} selected={selected.includes(l.id)} onToggle={toggle} cheapest={low != null && l.price === low}
                    onAdd={wo && wo.status !== "invoiced" ? () => addToWo(l) : null} />
                ))}
              </div>
            )}
          </section>
        )}
      </div>
      <CompareBar count={selected.length} onOpen={() => setCompareOpen(true)} onClear={() => setSelected([])} />
      <CompareDialog open={compareOpen} onOpenChange={setCompareOpen} listings={listings.filter((l) => selected.includes(l.id))} />
    </>
  );
}
